import React, { useState } from "react";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import { SUBSTACK_URL } from "../../mock/mock";

const HERO_IMG =
  "https://images.unsplash.com/photo-1529047033375-f402d3da24ca?auto=format&fit=crop&w=2400&q=80";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MARQUEE_PHRASES = ["Choose what's next", "Build your own path"];

const Hero = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | ok | err

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/subscribe`, { email, source: "hero" });
      setStatus("ok");
    } catch (err) {
      setStatus("err");
    }
    // Always open substack so user can complete signup there too
    window.open(SUBSTACK_URL, "_blank", "noreferrer");
    setEmail("");
    setTimeout(() => setStatus("idle"), 4000);
  };

  // Build marquee items alternating between the two phrases
  const marqueeItems = Array.from({ length: 18 }).map(
    (_, i) => MARQUEE_PHRASES[i % MARQUEE_PHRASES.length]
  );

  return (
    <section id="top" className="relative w-full h-screen min-h-[720px] overflow-hidden">
      {/* Full-bleed lifestyle background */}
      <div className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Warm morning light"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#41232E]/25 via-[#41232E]/5 to-transparent" />
      </div>

      {/* Content card — anchored bottom-left, above marquee */}
      <div className="relative h-full max-w-[1400px] mx-auto px-6 md:px-10 flex flex-col justify-end pb-[76px] md:pb-[88px]">
        <div
          className="om-fade-up bg-[#D8D355] rounded-[1.5rem] shadow-[0_10px_40px_rgba(65,35,46,0.18)] p-7 md:p-10 lg:p-11 w-full max-w-[480px] lg:max-w-[520px] mb-6 md:mb-8"
        >
          <h1 className="font-serif-display text-[#41232E] leading-[1.02] tracking-[-0.02em] text-[2.2rem] sm:text-[2.6rem] md:text-[3rem] lg:text-[3.3rem]">
            Your life is <em className="italic font-light">yours</em> to create.
          </h1>
          <p className="mt-5 text-[#41232E]/80 text-[0.95rem] md:text-base leading-relaxed max-w-md">
            Ideas, observations and practical frameworks for setting goals, making better
            decisions, creating more options &mdash; and making progress toward the life you
            actually want.
          </p>

          <form onSubmit={onSubmit} className="mt-7">
            <div className="flex items-center bg-[#FFFDF6] rounded-full pl-5 pr-1.5 py-1.5 border border-[#41232E]/20 focus-within:border-[#41232E] transition-colors">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@email.com"
                className="flex-1 bg-transparent outline-none py-2.5 md:py-3 text-[#41232E] placeholder:text-[#41232E]/45 text-base"
              />
              <button
                type="submit"
                aria-label="Subscribe"
                disabled={status === "loading"}
                className="shrink-0 w-11 h-11 rounded-full bg-[#41232E] text-[#FFFDF6] flex items-center justify-center hover:bg-[#2c161e] transition-colors disabled:opacity-70"
              >
                <ArrowRight size={18} />
              </button>
            </div>
            {status === "ok" && (
              <div className="mt-2 text-xs text-[#41232E]/80">Saved. Finishing on Substack&hellip;</div>
            )}
            {status === "err" && (
              <div className="mt-2 text-xs text-[#41232E]/80">
                Couldn&rsquo;t save right now &mdash; opening Substack anyway.
              </div>
            )}
          </form>

          <div className="mt-4 text-[0.85rem] text-[#41232E]/75">
            Already reading?{" "}
            <a
              href={SUBSTACK_URL}
              target="_blank"
              rel="noreferrer"
              className="underline decoration-[#41232E]/50 underline-offset-[3px] hover:decoration-[#41232E] transition-colors"
            >
              Manage your subscription
            </a>
          </div>
        </div>
      </div>

      {/* Marquee band flush at bottom of hero */}
      <div className="absolute bottom-0 left-0 right-0 bg-[#B995EE] py-2.5 overflow-hidden">
        <div className="overflow-hidden whitespace-nowrap">
          <div className="inline-flex om-marquee-track">
            {marqueeItems.map((phrase, i) => (
              <span
                key={i}
                className="px-6 text-[#41232E] font-medium tracking-[0.2em] uppercase text-xs"
              >
                {phrase} <span className="opacity-40 pl-6">/</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
